//Assignment.jsx
import React, { useEffect, useState,useCallback  } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, CheckCircle, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getAssignmentById,
  updateAssignmentStatus,
} from "../../../services/operations/assignmentAPI";
import { toast } from "react-hot-toast";
import AssignmentText from "../../../components/Dashboard/Student/AssignmentText";

const AssignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [assignment, setAssignment] = useState(null);
  const [detail, setDetail] = useState(null);
  const [score, setScore] = useState(0);

  const safeReplace = (str, from, to) =>
    typeof str === "string" ? str.replace(from, to) : str;

  const fetchAssignment = useCallback(async () => {
      if (!id) return;

      const data = await getAssignmentById(id)();
      const assignmentData = data?.data;
      if (!assignmentData) return;

      if (i18n.language.startsWith("te")) {
        setAssignment(assignmentData.telugu_version || {});
      } else {
        setAssignment(assignmentData.english_version || {});
      }

    setDetail(assignmentData);
  }, [id, i18n.language]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);


  const [expanded, setExpanded] = useState(false);

  const summary = assignment?.summary || t("assignmentDetail.na") || "N/A";
  const maxLength = 100;
  const isLong = summary.length > maxLength;
  const displayText = expanded
    ? summary
    : summary.slice(0, maxLength) + (isLong ? "..." : "");

  const handleStatusChange = async (newStatus) => {
    try {
      await updateAssignmentStatus(id, newStatus, score)();
      navigate("/dashboard/student");
    } catch (error) {
      toast.error(t("assignmentDetail.updateFailed"));
    }
  };

  if (!assignment)
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-500">
        {t("assignmentDetail.loading")}
      </div>
    );

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-3xl mx-auto bg-white shadow-md rounded-xl p-6 border border-gray-200">
        {/* 🔙 Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-gray-600 hover:text-blue-600 mb-4"
        >
          <ArrowLeft size={18} /> {t("assignmentDetail.back") || "Back"}
        </button>

        {/* 📄 Assignment Header */}
        <div className="flex items-center gap-3 mb-4">
          <FileText className="text-blue-600" size={28} />
          <h2 className="text-2xl font-semibold text-gray-800 capitalize">
            {safeReplace(assignment?.type, "_", " ") ||
              t("assignmentDetail.title") ||
              "Assignment"}
          </h2>
        </div>

        {/* 📅 Status and Created At */}
        <p className="text-gray-600 text-sm mb-4">
          <strong>{t("assignmentDetail.status") || "Status"}:</strong>{" "}
          {detail?.status
            ? detail?.status
            : t("assignmentDetail.na") || "N/A"}
        </p>

        <p className="text-gray-600 text-sm mb-4">
          <strong>{t("assignmentDetail.created") || "Created"}:</strong>{" "}
          {detail?.createdAt
            ? new Date(detail.createdAt).toLocaleString()
            : t("assignmentDetail.na") || "N/A"}
        </p>

        {/* Summary */}
        <p className="text-gray-600 text-sm mb-3">
          <strong>
            {t("assignmentDetail.summary") || "Assignment based on"}:
          </strong>{" "}
          <span className="capitalize text-blue-700">{displayText}</span>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="ml-2 text-blue-500 hover:underline text-xs"
            >
              {expanded
                ? t("assignmentDetail.showLess") || "Show less"
                : t("assignmentDetail.showMore") || "Show more"}
            </button>
          )}
        </p>

        {/* 🧾 Assignment Text */}
        <div className="mb-6">
          {/* <h3 className="text-lg font-semibold text-gray-700 mb-2">
            {t("assignmentDetail.assignmentText") || "Assignment Text"}
          </h3> */}
          <AssignmentText assignment={assignment} score= {score} setScore={setScore} />
        </div>

        {/* ✅ Mark as Completed Button */}
        <button
          onClick={() => handleStatusChange("completed")}
          disabled={assignment?.status === "completed"}
          className={`px-5 py-2 rounded-lg flex items-center gap-2 ${
            assignment?.status === "completed"
              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white"
          }`}
        >
          <CheckCircle size={18} />
          {assignment?.status === "completed"
            ? t("assignmentDetail.completed") || "Completed"
            : t("assignmentDetail.markCompleted") || "Mark as Completed"}
        </button>
      </div>
    </div>
  );
};

export default AssignmentDetail;
